import { getTestUrl } from "./get-test-url";
import { expect, test } from "@playwright/test";

type EventTestConfig = {
    testName: string;
    attribute: string;
    options: string[];
    component: string; // also selector for the component with 'ux' prefix
    testVariant?: string;
    theme?: "web" | "responsive";
};

/**
 * Defines a set of tests for a component with multiple attribute options.
 *
 * @param config - Configuration object for the test.
 * @param config.testName - The name of the test.
 * @param config.attribute - The attribute to be tested.
 * @param config.options - The options for the attribute.
 * @param config.component - The component to be tested (also used as a selector with 'ux' prefix).
 * @param config.testVariant - Optional variant of the test.
 * @param config.theme - Optional theme for the test ("web" or "responsive").
 *
 * @example
 * defineMultiOptionAttributeTest({
 *     testName: "Button color test",
 *     attribute: "color",
 *     options: ["red", "green", "blue"],
 *     component: "button",
 *     testVariant: "primary",
 *     theme: "web"
 * });
 */
export const defineMultiOptionAttributeTest = (config: EventTestConfig) => {
    for (const option of config.options) {
        test(`${config.testName} - ${option}`, async ({ page }) => {
            if (config.component && config.testVariant) {
                await page.goto(getTestUrl(config.component, config.testVariant, config.theme ? config.theme : "web"));
            }
            const element = page.locator(`ux-${config.component}.hydrated`);

            element.evaluate(
                (el, option) => {
                    el.setAttribute(option.attribute, option.option);
                },
                { option: option, attribute: config.attribute }
            );
            expect(await element.screenshot()).toMatchSnapshot();
            expect(await element.ariaSnapshot()).toMatchSnapshot();
        });
    }
};
